import { IaDrawer } from "ia-drawer";
import { updateCart, changeCart } from "ia-util-helpers";
import { EVENTS, subscribe, publish } from "ia-util-pubsub";

class IaMinicart extends IaDrawer {
  constructor() {
    super();
    console.log(">>> this overlay", this.overlay);
  }

  connectedCallback() {
    super.connectedCallback?.();
    this.sectionsKey = this.getAttribute("data-render-section") || "ia-mini-cart";
    console.log(">>> ia-mini-cart loaded");
  }

  get preloading() {
    return this.hasAttribute("data-preloading");
  }

  set preloading(value) {
    this.toggleAttribute("data-preloading", value);
  }

  preload() {
    if (this.preloading) return;

    this.preloading = true;
    this.classList.add("loading");

    setTimeout(() => {
      if (this.preloading) {
        this.preloadDone();
      }
    }, 5000);
  }

  preloadDone() {
    this.preloading = false;
    this.classList.remove("loading");
  }

  async updateQty(key, qty, _ts = null) {
    // When removing (qty === 0) with _ts: update all items in the group (parent + all add-ons)
    if (_ts && qty === 0) {
      await this.updateQtyBulk(_ts, qty);
      return;
    }
    // When changing quantity with _ts: sync only parent + switch add-on children (same _ts)
    if (_ts && qty > 0) {
      await this.updateQtyWithSwitchSync(key, qty, _ts);
      return;
    }

    let cartData = null;

    const updates = { [key]: qty };
    const sections = this.sectionsKey;
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections });
    try {
      cartData = await updateCart({
        data: { updates, sections },
      });
      this.render(cartData);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  /**
   * Updates parent item and only its switch add-on children (same _ts). Other add-ons (e.g. bulb) are unchanged.
   */
  async updateQtyWithSwitchSync(key, qty, ts) {
    const updates = { [key]: qty };
    const switchAddonItems = Array.from(
      this.querySelectorAll("ia-mini-cart-item[data-ts][data-switch-addon]")
    ).filter((item) => item.dataset.ts === ts && item.dataset.key !== key);
    for (const item of switchAddonItems) {
      updates[item.dataset.key] = qty;
    }

    const sections = this.sectionsKey;
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections });
    let cartData = null;
    try {
      cartData = await updateCart({
        data: { updates, sections },
      });
      this.render(cartData);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  async updateQtyBulk(ts, qty) {
    const itemsToRemove = Array.from(
      this.querySelectorAll("ia-mini-cart-item")
    ).filter((item) => item.dataset.ts === ts);
    const keys = itemsToRemove.map((item) => item.dataset.key);
    const updates = Object.fromEntries(keys.map((key) => [key, qty]));
    const sections = this.sectionsKey;
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections });
    let cartData = null;
    try {
      cartData = await updateCart({
        data: {
          updates,
          sections: sections,
        },
      });
      this.render(cartData);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  async changePlan({ key, planId, qty }) {
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections });
    let cartData = null;
    try {
      cartData = await changeCart({
        id: key,
        quantity: qty,
        planId,
        sections: sections,
      });
      this.render(cartData);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  render(cartData) {
    const sections = this.sectionsKey;
    const html = cartData.sections?.[sections];
    if (!html) {
      console.error(`Missing section HTML for: ${sections}`);
      return;
    }
    const newWindowContent = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(".js-mini-cart-inner");
    const oldWindowContent = this.querySelector(".js-mini-cart-inner");

    if (!newWindowContent || !oldWindowContent) {
      console.error("Could not find drawer window content to update.");
      return;
    }

    oldWindowContent.innerHTML = newWindowContent.innerHTML;

    this.preloadDone();
    // Update cart count
    const cartCount = newWindowContent.dataset.itemCount || "0";
    console.log("cartCount", cartCount);
    publish(EVENTS.cartRendered, { detail: { cartData, cartCount } });
  }

  open(cartData) {
    if (cartData) {
      this.render(cartData);
    }
    super.open();
  }
}

class IaMinicartItem extends HTMLElement {
  connectedCallback() {
    this.minicart = document.querySelector("ia-mini-cart");
    this.key = this.dataset.key;
    this.ts = this.dataset.ts || null;
    this.removeButton = this.querySelector(".js-cart-item-remove");
    // Listen for qty-picker changes
    this.qtyPicker = this.querySelector("ia-qty-picker");
    if (this.qtyPicker) {
      this.unsubscribeQtyChange = subscribe(
        EVENTS.qtyChange,
        this.handleQtyChange.bind(this)
      );
    }

    this.removeButton?.addEventListener("click", this.handleRemove.bind(this));
    this.querySelector("[data-change-plan]")?.addEventListener(
      "change",
      this.handlePlanChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.unsubscribeQtyChange?.();
  }

  handleQtyChange(event) {
    const { value, id } = event.detail;
    if (id === this.key) {
      this.minicart?.updateQty(this.key, value, this.ts);
    }
  }

  handleRemove() {
    this.minicart?.updateQty(this.key, 0, this.ts);
  }

  handlePlanChange(event) {
    const planId = event.target.value;
    const qty = this.dataset.qty;
    this.minicart?.changePlan({ key: this.key, planId, qty });
  }
}

class IaCartBubble extends HTMLElement {
  connectedCallback() {
    this.unsubscribeCartRendered = subscribe(
      EVENTS.cartRendered,
      this.handleCartRendered.bind(this)
    );
  }

  disconnectedCallback() {
    this.unsubscribeCartRendered?.();
  }

  handleCartRendered(event) {
    console.log("handleCartRendered", event.detail);
    const { cartCount } = event.detail;
    if (cartCount !== undefined) {
      this.textContent = cartCount;
      this.classList.toggle("ia-hidden", cartCount * 1 === 0);
    }
  }
}

class IaCartNote extends HTMLElement {
  connectedCallback() {
    this.accordion = this.querySelector("ia-accordion-component");
    this.cart = this.closest("ia-mini-cart");
    this.sectionsKey = this.cart.getAttribute("data-render-section") || "ia-mini-cart";
    // Add note update button listener
    this.querySelector(".js-note-update")?.addEventListener(
      "click",
      this.handleNoteUpdate.bind(this)
    );
  }
  async handleNoteUpdate(event) {
    event.preventDefault();

    const noteTextarea = this.querySelector("#cart-note");
    if (!noteTextarea) return;

    const note = noteTextarea.value;

    this.cart.preload();

    try {
      const cartData = await updateCart({
        data: {
          note,
          sections: this.sectionsKey,
        },
      });
      this.cart.render(cartData);
    } catch (error) {
      console.error("Error updating cart note:", error);
    } finally {
      this.cart.preloadDone();
    }
  }
}

customElements.define("ia-mini-cart", IaMinicart);
customElements.define("ia-mini-cart-item", IaMinicartItem);
customElements.define("ia-cart-bubble", IaCartBubble);
customElements.define("ia-cart-note", IaCartNote);

// No need for default export if not importing elsewhere
